import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, PageHeader, SectionCard, Spinner, Toggle, useDebounce } from '../components/ui';
import { fmtDate, fmtMoney, fmtNumber, timeAgo } from '../lib/format';

interface GameUser {
  id: string;
  nickname: string;
  balance: number;
  isDealer: boolean;
  peekingEnabled: boolean;
  online: boolean;
  lastSeen: number;
  createdAt: number;
  iconId: string | null;
  avatarUrl: string | null;
}

interface AdjustModalState {
  user: GameUser;
  amount: string;
  reason: string;
  error: string | null;
}

export function UsersPage() {
  const [items, setItems] = useState<GameUser[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'online' | 'dealer'>('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adjustModal, setAdjustModal] = useState<AdjustModalState | null>(null);
  const debouncedSearch = useDebounce(search, 250);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filter === 'online') params.set('online', 'true');
    api
      .get<{ items: GameUser[]; total: number }>(`/api/crm/users?${params.toString()}`)
      .then((r) => {
        let arr = r.items;
        if (filter === 'dealer') arr = arr.filter((u) => u.isDealer);
        setItems(arr);
        setTotal(r.total);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [debouncedSearch, filter]);

  const adjust = async (u: GameUser, delta: number, reason?: string) => {
    setBusyId(u.id);
    try {
      await api.post(`/api/crm/users/${u.id}/balance`, { delta, reason: reason || undefined });
      load();
    } catch (e: any) {
      alert(e?.error === 'NEGATIVE_BALANCE' ? 'Баланс не может уйти ниже 0' : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  const openCustomAdjust = (u: GameUser) => {
    setAdjustModal({ user: u, amount: '', reason: '', error: null });
  };

  const submitCustomAdjust = async () => {
    if (!adjustModal) return;
    const raw = adjustModal.amount.trim().replace(',', '.');
    const num = Number(raw);
    if (!raw || !Number.isFinite(num) || num === 0) {
      setAdjustModal({ ...adjustModal, error: 'Введите ненулевую сумму (можно с минусом для списания)' });
      return;
    }
    if (!Number.isInteger(num)) {
      setAdjustModal({ ...adjustModal, error: 'Сумма должна быть целым числом' });
      return;
    }
    const u = adjustModal.user;
    setBusyId(u.id);
    try {
      await api.post(`/api/crm/users/${u.id}/balance`, { delta: num, reason: adjustModal.reason || undefined });
      setAdjustModal(null);
      load();
    } catch (e: any) {
      const msg = e?.error === 'NEGATIVE_BALANCE' ? 'Баланс не может уйти ниже 0' : 'Ошибка';
      setAdjustModal({ ...adjustModal, error: msg });
    } finally {
      setBusyId(null);
    }
  };

  const toggleDealer = async (u: GameUser) => {
    setBusyId(u.id);
    try {
      await api.post(`/api/crm/users/${u.id}/dealer`, { isDealer: !u.isDealer });
      load();
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <PageHeader
        title="Пользователи"
        subtitle={`Всего: ${fmtNumber(total)}`}
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Icon.Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                className="input pl-9 w-56"
                placeholder="Поиск по нику…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select className="input w-40" value={filter} onChange={(e) => setFilter(e.target.value as any)}>
              <option value="all">Все</option>
              <option value="online">Онлайн</option>
              <option value="dealer">Крупье</option>
            </select>
          </div>
        }
      />

      <SectionCard>
        {loading ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-6"><Spinner /> Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-stone-400 py-8 text-center">Пользователи не найдены</div>
        ) : (
          <div className="overflow-x-auto -mx-3">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Ник</th>
                  <th>Баланс</th>
                  <th>Статус</th>
                  <th>Крупье</th>
                  <th>Пик</th>
                  <th>Был онлайн</th>
                  <th>Создан</th>
                  <th className="text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20 flex items-center justify-center text-sm font-semibold">
                          {u.nickname.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{u.nickname}</div>
                          <div className="text-[11px] text-stone-500 font-mono">{u.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="font-mono">{fmtMoney(u.balance)}</td>
                    <td>
                      {u.online ? <Badge tone="green">онлайн</Badge> : <Badge tone="stone">оффлайн</Badge>}
                    </td>
                    <td>
                      <Toggle pressed={u.isDealer} onChange={() => toggleDealer(u)} disabled={busyId === u.id} ariaLabel="Крупье" />
                    </td>
                    <td>
                      {u.peekingEnabled ? <Badge tone="amber"><Icon.Eye size={11} />peek</Badge> : <span className="text-stone-500 text-xs">—</span>}
                    </td>
                    <td className="text-stone-400 text-xs">{timeAgo(u.lastSeen)}</td>
                    <td className="text-stone-400 text-xs">{fmtDate(u.createdAt)}</td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        <button className="btn btn-secondary !px-2.5 !py-1.5" disabled={busyId === u.id} onClick={() => adjust(u, 1000)}>+1000</button>
                        <button className="btn btn-secondary !px-2.5 !py-1.5" disabled={busyId === u.id} onClick={() => adjust(u, 5000)}>+5000</button>
                        <button className="btn btn-secondary !px-2.5 !py-1.5" disabled={busyId === u.id} onClick={() => adjust(u, -1000)}>-1000</button>
                        <button
                          className="btn btn-gold !px-2.5 !py-1.5"
                          disabled={busyId === u.id}
                          onClick={() => openCustomAdjust(u)}
                          title="Начислить или списать произвольную сумму"
                        >
                          ±
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {adjustModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => !busyId && setAdjustModal(null)}
        >
          <div
            className="glass-strong rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-400 mb-1">Корректировка баланса</div>
              <div className="text-lg font-semibold text-stone-100">{adjustModal.user.nickname}</div>
              <div className="text-xs text-stone-500 font-mono">{adjustModal.user.id.slice(0, 8)} · текущий: <span className="font-mono">{fmtMoney(adjustModal.user.balance)}</span></div>
            </div>

            <div>
              <label className="block text-xs text-stone-400 mb-1.5">Сумма (минус = списание)</label>
              <input
                className="input w-full text-lg font-mono"
                type="number"
                step="1"
                placeholder="напр. 2500 или -1500"
                value={adjustModal.amount}
                autoFocus
                onChange={(e) => setAdjustModal({ ...adjustModal, amount: e.target.value, error: null })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCustomAdjust();
                  if (e.key === 'Escape') setAdjustModal(null);
                }}
              />
              <div className="flex gap-1.5 mt-2">
                {[100, 500, 1000, 2500, 5000].map((n) => (
                  <button
                    key={`p${n}`}
                    type="button"
                    className="btn btn-ghost !px-2 !py-1 !text-xs"
                    onClick={() => setAdjustModal({ ...adjustModal, amount: String(n), error: null })}
                  >
                    +{n}
                  </button>
                ))}
                {[100, 500, 1000].map((n) => (
                  <button
                    key={`m${n}`}
                    type="button"
                    className="btn btn-ghost !px-2 !py-1 !text-xs"
                    onClick={() => setAdjustModal({ ...adjustModal, amount: String(-n), error: null })}
                  >
                    -{n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs text-stone-400 mb-1.5">Причина <span className="text-stone-600">(необязательно)</span></label>
              <input
                className="input w-full"
                placeholder="напр. бонус за активность"
                value={adjustModal.reason}
                onChange={(e) => setAdjustModal({ ...adjustModal, reason: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCustomAdjust();
                  if (e.key === 'Escape') setAdjustModal(null);
                }}
              />
            </div>

            {adjustModal.error && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {adjustModal.error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                className="btn btn-ghost"
                disabled={busyId === adjustModal.user.id}
                onClick={() => setAdjustModal(null)}
              >
                Отмена
              </button>
              <button
                className="btn btn-gold"
                disabled={busyId === adjustModal.user.id}
                onClick={submitCustomAdjust}
              >
                {busyId === adjustModal.user.id ? <Spinner /> : 'Применить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
